import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Loader } from '@elfsight/widget-ui';
import classNames from 'classnames';
import {
  APP_CLASSNAME_PREFIX,
  SUPPORT_PROVIDERS,
  VIDEO_ASPECT_RATIO,
  YOUTUBE_DEFAULT_HEIGHT
} from '../../../../constants';
import { getVideoURL } from '../../../helpers';
import { Player } from '../Player';

const getMaxMediaData = (acc, node) => {
  if (!node) {
    return acc;
  }

  const { width, height } = node.getBoundingClientRect();
  return {
    width: Math.max(width, acc.width),
    height: Math.max(height, acc.height)
  };
};

export const Media = ({
  data,
  active,
  isMobile,
  isVerticalLayout,
  isHorizontalLayout,
  absolute,
  cloned,
  mediaData,
  setMediaData
}) => {
  const {
    contentMediaType,
    contentMediaImage,
    contentMediaVideo,
    contentImageSize,
    alt,
    contentLayout
  } = data;

  const [userPlayVideo, setUserPlayVideo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const itemRef = useRef(null);
  const containerRef = useRef(null);
  const loaderRef = useRef(null);

  const { current: containerCurrent } = containerRef;
  const { current: itemCurrent } = itemRef;

  const isVideo = contentMediaType === 'video';
  const contentMedia = isVideo ? contentMediaVideo : contentMediaImage;
  const isUrlVideo = contentMedia?.type === 'url';
  const fixedMedia = isVideo ? contentMedia?.originalValue : contentMedia?.url;
  const paddingTop = VIDEO_ASPECT_RATIO * contentImageSize;
  const scale = contentImageSize / 100;
  const isContainerWiderThanContent = useMemo(
    () => containerCurrent?.clientWidth > itemCurrent?.clientWidth * scale,
    [containerCurrent?.clientWidth, itemCurrent?.clientWidth, scale]
  );

  const type =
    SUPPORT_PROVIDERS.find((item) => item === contentMedia?.type) || 'image';

  const handleUpdateMediaData = useCallback(() => {
    if (!itemRef.current) {
      return;
    }

    const nodes =
      type === 'image'
        ? [loaderRef.current, itemRef.current]
        : [itemRef.current];

    const initialData = { width: 0, height: 0 };
    const maxMediaData = nodes.reduce(getMaxMediaData, initialData);

    setMediaData(maxMediaData);
    // oxlint-disable-next-line exhaustive-deps
  }, [type]);

  const handleImageLoad = useCallback(() => {
    if (!imageLoaded) {
      setImageLoaded(true);
      handleUpdateMediaData();
    }
  }, [handleUpdateMediaData, imageLoaded]);

  const handleDrag = useCallback((e) => e.preventDefault(), []);

  useEffect(() => {
    if (!contentMedia && imageLoaded) {
      setImageLoaded(false);
    }
  }, [contentMedia, imageLoaded]);

  useEffect(handleUpdateMediaData, [contentImageSize, handleUpdateMediaData]);

  if (!contentMedia) {
    return null;
  }

  const transformOrigin =
    isContainerWiderThanContent && isHorizontalLayout
      ? 'center'
      : contentLayout;

  return (
    <Container
      ref={containerRef}
      $isContainerWider={isContainerWiderThanContent}
      $contentLayout={contentLayout}
      $isMobile={isMobile}
      $aspectRatio={mediaData.width / mediaData.height}
      $isFixedOrBottom={fixedMedia || contentLayout === 'bottom'}
      $isVerticalLayout={isVerticalLayout}
      $mediaHeight={mediaData.height}
    >
      {type === 'image' ? (
        <>
          <StyledImage
            ref={itemRef}
            src={fixedMedia}
            alt={alt || 'media'}
            onDragStart={handleDrag}
            onLoad={handleImageLoad}
            $loaded={imageLoaded}
            $isMobile={isMobile}
            $scale={scale}
            $transformOrigin={transformOrigin}
            $absolute={absolute}
          />

          {!imageLoaded && (
            <LoaderContainer ref={loaderRef}>
              <Loader
                className={`${APP_CLASSNAME_PREFIX}-spinner-component`}
                color="#fff"
              />
            </LoaderContainer>
          )}
        </>
      ) : (
        <Inner
          ref={itemRef}
          $size={contentImageSize}
          $paddingTop={paddingTop}
          $isUrlVideo={isUrlVideo}
          $hasId={!!contentMedia?.id}
        >
          {!cloned && (
            <Player
              controls
              playing={active && userPlayVideo}
              videoId={
                contentMedia.id ?? contentMedia.originalValue.split('/').pop()
              }
              url={getVideoURL(contentMedia)}
              height="auto"
              onPlay={() => setUserPlayVideo(true)}
              onPause={() => setUserPlayVideo(false)}
              onReady={handleUpdateMediaData}
            />
          )}
        </Inner>
      )}
    </Container>
  );
};

const Container = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-media-container`
})`
  width: 50%;
  z-index: 1;
  position: relative;
  transition:
    0.3s ease top,
    0.3s ease opacity;
  top: -10px;
  opacity: 0;
  padding: 0 20px;
  box-sizing: border-box;
  height: 100%;
  flex-shrink: 1;
  text-align: center;
  display: flex;
  justify-content: center;

  .${APP_CLASSNAME_PREFIX}-slide-active.${APP_CLASSNAME_PREFIX}-slide-animate
    & {
    top: 0;
    opacity: 1;
  }

  .${APP_CLASSNAME_PREFIX}-slide-top &,
  .${APP_CLASSNAME_PREFIX}-slide-bottom &,
  .${APP_CLASSNAME_PREFIX}-slide-center & {
    width: 100%;
  }

  .${APP_CLASSNAME_PREFIX}-slide-right &,
  .${APP_CLASSNAME_PREFIX}-slide-left & {
    align-items: center;
  }

  .${APP_CLASSNAME_PREFIX}-slide-bottom & {
    align-items: flex-end;
  }

  justify-content: ${({ $isContainerWider, $contentLayout }) => {
    if ($isContainerWider) {
      return 'center';
    }

    if ($contentLayout === 'left') {
      return 'flex-start';
    }

    if ($contentLayout === 'right') {
      return 'flex-end';
    }

    return 'center';
  }};

  ${({ $isMobile, $aspectRatio, $isFixedOrBottom }) =>
    $isMobile &&
    css`
      width: 100%;
      height: auto;
      aspect-ratio: ${$aspectRatio};
      flex-grow: 1;
      align-items: ${$isFixedOrBottom ? 'center' : 'flex-start'};
    `}

  ${({ $isMobile, $isVerticalLayout, $mediaHeight }) =>
    !$isMobile &&
    $isVerticalLayout &&
    css`
      height: ${$mediaHeight}px;
    `}
`;

const StyledImage = styled.img.attrs(({ $loaded }) => ({
  className: classNames(`${APP_CLASSNAME_PREFIX}-media-component`, {
    [`${APP_CLASSNAME_PREFIX}-media-loaded`]: $loaded
  })
}))`
  -webkit-user-drag: none;
  position: absolute;
  opacity: 0;
  transition: 0.3s ease opacity;

  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};

  ${({ $isMobile, $scale, $transformOrigin, $absolute }) =>
    $isMobile
      ? css`
          max-width: 100%;
          left: 50%;
          transform: translateX(-50%);
          position: absolute;
        `
      : css`
          transform: scale(${$scale});
          transform-origin: ${$transformOrigin};
          ${$absolute && 'position: absolute;'}
        `}
`;

const Inner = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-media-inner`
})`
  width: 100%;
  position: relative;
  width: ${({ $size }) => $size}%;

  & iframe {
    position: absolute;
    left: 0;
    top: 0;
  }

  ${({ $isUrlVideo, $paddingTop }) =>
    !$isUrlVideo &&
    css`
      padding-top: ${$paddingTop}%;
    `}

  ${({ $hasId }) =>
    !$hasId &&
    css`
      min-height: ${YOUTUBE_DEFAULT_HEIGHT}px;
    `}
`;

const LoaderContainer = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-media-loaderContainer`
})`
  padding: 20px;
`;
